import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { API } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight, Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be 6+ characters."); return; }
    if (pw !== pw2) { toast.error("Passwords don't match."); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API}/member/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: pw }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || "Reset failed");
      toast.success("Password updated. Sign in below.");
      navigate("/member/login");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white grid place-items-center px-6">
        <div className="max-w-md w-full border border-zinc-800 bg-[#141414] p-10 text-center">
          <h1 className="font-display text-3xl mb-3">Invalid Link</h1>
          <p className="text-zinc-400 text-sm mb-6">The reset link is missing a token. Request a new one.</p>
          <Link to="/member/forgot-password" className="text-[#E5B43A] text-xs font-bold uppercase tracking-widest hover:underline">Request New Link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grid place-items-center px-6" data-testid="reset-password-page">
      <form onSubmit={submit} className="w-full max-w-md border border-zinc-800 bg-[#141414] p-8 md:p-10" data-testid="reset-form">
        <div className="flex items-center gap-3 mb-8">
          <span className="w-10 h-10 bg-[#E5B43A] grid place-items-center">
            <ShieldCheck size={18} className="text-black" />
          </span>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Secure</div>
            <div className="font-display text-xl tracking-wider">Set New Password</div>
          </div>
        </div>

        <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">New Password</label>
        <div className="relative mb-4">
          <input
            type={show ? "text" : "password"}
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            required
            minLength={6}
            data-testid="new-password-input"
            className="w-full bg-[#0F0F0F] border border-zinc-800 px-4 py-3 pr-12 text-white focus:outline-none focus:border-[#E5B43A]"
          />
          <button type="button" onClick={() => setShow((s) => !s)} className="absolute inset-y-0 right-0 px-3 flex items-center text-zinc-500 hover:text-[#E5B43A]">
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Confirm</label>
        <input
          type={show ? "text" : "password"}
          value={pw2}
          onChange={(e) => setPw2(e.target.value)}
          required
          minLength={6}
          data-testid="confirm-password-input"
          className="w-full bg-[#0F0F0F] border border-zinc-800 px-4 py-3 mb-6 text-white focus:outline-none focus:border-[#E5B43A]"
        />

        <button
          type="submit"
          disabled={loading}
          data-testid="reset-submit"
          className="w-full inline-flex items-center justify-center gap-3 bg-[#E5B43A] text-black font-bold uppercase tracking-widest px-6 py-4 text-sm hover:bg-white hover:text-[#0A0A0A] transition-colors disabled:opacity-60"
        >
          {loading ? "Updating..." : <>Update Password <ArrowRight size={16} /></>}
        </button>
      </form>
    </div>
  );
}
